import React, { useEffect } from "react";
// AOS Animation NPM
import AOS from "aos";
import "aos/dist/aos.css";
import './App.scss';

import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Main Layout and Pages
import { Layout } from './components/layouts/Layout';
import { AboutUs } from './pages/AboutUs';
import { ContactUs } from './pages/ContactUs';
import { Communities } from './pages/communities/Communities';
import { CreateCommunity } from './pages/communities/CreateCommunity';
import { CommunityDetail } from './pages/communities/CommunityDetail';
import { CommunityDetailGuest } from './pages/communities/CommunityDetailGuest';
import { Features } from './pages/Features';
import { Pricing } from './pages/Pricing';
import { PageNotFound } from './pages/PageNotFound';
import Privacy from './pages/privacy-policy/Privacy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import Login from './pages/authentication/Login';
import SignUp from './pages/authentication/SignUp';
import ForgotPassword from './pages/authentication/ForgotPassword';
import VerifyOTP from './pages/authentication/VerifyOTP';
import ResetPassword from './pages/authentication/ResetPassword';
import ScrollToTopOnRouteChange from "./components/ScrollToTopOnRouteChange";
import { DeepLinkRedirect } from "./components/DeepLinkRedirect";
import Marketplace from './pages/marketplace/Marketplace';
import { Home } from "./pages/Homepage/Home";
import { TeachersDetails } from "./pages/teachers-section/teachers-details/TeachersDetails";
import { Teachers } from './pages/teachers/Teachers';
import { TeachersGuest } from './pages/teachers/TeachersGuest';
import { TeachersDetailsGuest } from './pages/teachers-section/teachers-details/TeachersDetailsGuest';

// New Pages
import { ClassesPage } from './pages/classes/ClassesPage';
import { ClassDetail } from './pages/classes/ClassDetail';
import { ClassDetailGuest } from './pages/classes/ClassDetailGuest';
import { CreateClass } from './pages/classes/CreateClass';
import { CoursesPage } from './pages/courses/CoursesPage';
import { CourseDetail } from './pages/courses/CourseDetail';
import { CourseDetailGuest } from './pages/courses/CourseDetailGuest';
import { CoursePlayer } from './pages/courses/CoursePlayer';
import { EventDetail } from './pages/events/EventDetail';
import { LessonDetail } from './pages/lessons/LessonDetail';
import { UserProfile } from './pages/user-profile/UserProfile';
import { StudentDetails } from './pages/user-profile/StudentDetails';
import { AppRedirect } from './pages/app-redirect/AppRedirect';
import { VideoFeed } from './pages/video-feed/VideoFeed';
import { VideoFeedGuest } from './pages/video-feed/VideoFeedGuest';
import { Onboarding } from './pages/onboarding/Onboarding';
import { RevenueDashboard } from './pages/revenue/RevenueDashboard';
import { PracticeDashboard } from './pages/practice/PracticeDashboard';
import { PracticeMatching } from './pages/practice/PracticeMatching';
import { PracticeAIBuddy } from './pages/practice/PracticeAIBuddy';
import { NotificationsPage } from './pages/notifications/NotificationsPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { EnquiryPage } from './pages/enquiry/EnquiryPage';

function App() {
  // AOS Animations
  useEffect(() => {
    AOS.init({
      offset: 50,
      duration: 800,
      easing: 'ease-in-out',
      once: true,
      mirror: false,
    });
  }, []);

  const { isAuthenticated } = useSelector((state) => state.auth || {});
  const hasToken = localStorage.getItem('authToken') && localStorage.getItem('authToken') !== 'null' && localStorage.getItem('authToken') !== 'undefined';
  const isGuest = !isAuthenticated && !hasToken;

  return (
    <>
      <BrowserRouter>
        {/* When redirect to any page, page should load from top */}
        <ScrollToTopOnRouteChange />

        <Routes>

          {/* Deeplink Routes - Must come before catch-all */}
          <Route path="/deeplink/*" element={<DeepLinkRedirect />} />
          <Route path="/teacher/:id" element={<DeepLinkRedirect />} />
          <Route path="/video/:id" element={<DeepLinkRedirect />} />
          <Route path="/community/:id" element={<DeepLinkRedirect />} />
          <Route path="/live/:id" element={<DeepLinkRedirect />} />
          <Route path="/teacher/:id" element={<DeepLinkRedirect />} />

          {/* Authentication & Onboarding Routes - Outside Layout for focused UI */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/course-player" element={<CoursePlayer />} />
          <Route path="/course-detail" element={isGuest ? <CourseDetailGuest /> : <CourseDetail />} />

          {/* Main Layout Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<AboutUs />} />
            <Route path="marketplace" element={<Marketplace />} />

            {/* Community Section - fully on website */}
            <Route path="communities" element={<Communities />} />
            <Route path="community-detail" element={isGuest ? <CommunityDetailGuest /> : <CommunityDetail />} />
            <Route path="create-community" element={<CreateCommunity />} />
            <Route path="edit-community" element={<CreateCommunity />} />

            {/* Classes Section - website */}
            <Route path="classes" element={<ClassesPage />} />
            <Route path="class-detail" element={isGuest ? <ClassDetailGuest /> : <ClassDetail />} />
            <Route path="create-class" element={<CreateClass />} />
            <Route path="edit-class" element={<CreateClass />} />

            {/* Full Courses Section - website */}
            <Route path="courses" element={<CoursesPage />} />

            {/* Practice Section - website */}
            <Route path="practice" element={<PracticeDashboard />} />
            <Route path="practice/matching" element={<PracticeMatching />} />
            <Route path="practice/ai-buddy" element={<PracticeAIBuddy />} />

            {/* New specific detail pages for Events and Lessons */}
            <Route path="event-detail" element={<EventDetail />} />
            <Route path="lesson-detail" element={<LessonDetail />} />

            {/* Teacher Listing & Profiles - website */}
            <Route path="teachers" element={isGuest ? <TeachersGuest /> : <Teachers />} />
            <Route path="teachers-details" element={isGuest ? <TeachersDetailsGuest /> : <TeachersDetails />} />

            {/* Account Management */}
            <Route path="profile" element={<UserProfile />} />
            <Route path="student" element={<StudentDetails />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="video-feed" element={isGuest ? <VideoFeedGuest /> : <VideoFeed />} />
            <Route path="revenue" element={<RevenueDashboard />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="enquiry" element={<EnquiryPage />} />

            {/* App-only sections (deep-link redirects) */}
            <Route path="feed" element={<AppRedirect section="feed" />} />
            <Route path="practice" element={<AppRedirect section="practice" />} />
            <Route path="friends" element={<AppRedirect section="friends" />} />

            <Route path="features" element={<Features />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="contact" element={<ContactUs />} />
            <Route path="privacy-policy" element={<Privacy />} />
            <Route path="terms-of-service" element={<TermsOfService />} />
            <Route path="refund-policy" element={<RefundPolicy />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>

        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;
